import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import useSWR from "swr";

import {
  Box,
  Button,
  Flex,
  IconCross,
  NoSSR,
  Text,
} from "@catchoftheday/cg-components";
import { useMedia } from "@catchoftheday/utilities";

import { fetcher } from "../../api/index";
import { HEADER_COUPONS_API } from "../../consts";
import { AxiosResponse } from "../../types/index";
import { sessionStorage } from "../../utilities";
import theme from "../theme";

import { CouponDetailsModal } from "./CouponDetailsModal/CouponDetailsModal";
import { trackCouponClick, trackCouponImpression } from "./utilities/analytics";
import { Coupon } from "./types";

const STORAGE_KEY = "couponNotificationVisible";

type CouponApiResponse = {
  coupons: Coupon[];
  customerId: number;
};

type CouponNotificationProps = {
  displayName?: string;
};

export const CouponNotification = ({
  displayName,
}: CouponNotificationProps) => {
  const hasTrackedImpression = useRef<boolean>(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const isDesktop = useMedia<boolean>(
    [`(min-width: ${theme.breakpoints.lg})`],
    [true]
  );
  const debouncedTrackCouponClick = debounce(trackCouponClick, 500);

  const { data: customerCouponsData } = useSWR<
    AxiosResponse<CouponApiResponse>
  >(isDesktop && isVisible ? HEADER_COUPONS_API : null, fetcher);

  const activeCoupon = useMemo(() => {
    if (
      !customerCouponsData ||
      (customerCouponsData && !customerCouponsData.data?.coupons?.length)
    ) {
      return null;
    }

    return {
      ...customerCouponsData.data.coupons[0],
      customerId: customerCouponsData.data.customerId,
    };
  }, [customerCouponsData]);

  const handleCloseButtonClick = () => {
    if (sessionStorage) {
      sessionStorage.setItem(STORAGE_KEY, "false");
    }
    setIsVisible(false);
  };

  const handleCouponCodeClick = useCallback(() => {
    if (activeCoupon) {
      debouncedTrackCouponClick({
        couponData: activeCoupon,
        customerId: activeCoupon.customerId,
      });
    }
  }, [activeCoupon, debouncedTrackCouponClick]);

  const toggleCouponModal = useCallback(() => {
    if (activeCoupon && !isCouponModalOpen) {
      debouncedTrackCouponClick({
        couponData: activeCoupon,
        customerId: activeCoupon.customerId,
      });
    }
    setIsCouponModalOpen((prevState) => !prevState);
  }, [activeCoupon, debouncedTrackCouponClick, isCouponModalOpen]);

  useEffect(() => {
    if (sessionStorage) {
      const isNotificationVisible =
        sessionStorage.getItem(STORAGE_KEY) === "false" ? false : true;

      setIsVisible(isNotificationVisible);
    }
  }, []);

  useEffect(() => {
    if (activeCoupon && !hasTrackedImpression.current) {
      trackCouponImpression({
        couponData: activeCoupon,
        customerId: activeCoupon.customerId,
      });
      hasTrackedImpression.current = true;
    }
  }, [activeCoupon]);

  if (!activeCoupon || !isDesktop || !isVisible || !activeCoupon.code.length) {
    return null;
  }

  return (
    <NoSSR>
      <Box
        role="alert"
        position="relative"
        width="100%"
        py="10px"
        bg="#F9EBBD"
        fontSize="14px"
        textAlign="center"
      >
        <Flex justifyContent="center">
          <Text as="div" mr="3px">
            {displayName ? `Hey ${displayName},` : "Hey!"}{" "}
          </Text>
          {activeCoupon.salesRule.shortDescription && (
            <Text as="div" mr="3px" textTransform="lowercase">
              {activeCoupon.salesRule.shortDescription} |{" "}
            </Text>
          )}
          <Box
            mr="3px"
            fontWeight="medium"
            cursor="text"
            onClick={handleCouponCodeClick}
          >{`USE CODE: ${activeCoupon.code}`}</Box>
          <Button
            variant="plain"
            p="0px"
            fontSize="14px"
            border="0px"
            minWidth={{ xs: "auto" }}
            onClick={toggleCouponModal}
          >
            <Text
              lineHeight="1.4"
              m="0px"
              textTransform="capitalize"
              textDecoration="underline"
            >
              Details
            </Text>
          </Button>
        </Flex>
        <Button
          variant="plain"
          position="absolute"
          right="18px"
          top="calc(50% - 6px)"
          lineHeight="12px"
          border="0px"
          p="0px"
          minWidth={{ xs: "0px" }}
          onClick={handleCloseButtonClick}
        >
          <IconCross width="12px" height="12px" />
        </Button>
      </Box>
      <CouponDetailsModal
        isOpen={isCouponModalOpen}
        displayName={displayName}
        handleModalClose={toggleCouponModal}
        {...activeCoupon}
      />
    </NoSSR>
  );
};
